/* SpawnAnything — "Foto" button + dialog: photo (file / drag & drop / phone camera) -> comic voxel unit.
   Usage: createPhotoUpload({ mount: genRowElement, onRecipe: (team, result) => {...} })
   The photo is downscaled and re-encoded in the browser (prepareImage) and only kept in memory until the dialog closes. */
import { prepareImage, generatePhoto, HINT_MAX } from '../net/generatePhoto.js';

const CSS = `
.awp{border:1px solid var(--line);border-radius:8px;background:var(--panel-solid,#121822);color:var(--fg,#eef2f8);padding:16px;width:min(420px,calc(100vw - 32px));max-height:calc(100dvh - 32px);overflow:auto}
.awp::backdrop{background:rgba(4,6,10,.6)}
.awp h2{margin:0 0 10px;font-family:var(--f-display);font-size:17px;font-weight:400;letter-spacing:.02em}
.awp .awp-drop{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;min-height:150px;border:1.5px dashed var(--line);border-radius:6px;background:var(--tint);padding:10px;text-align:center;color:var(--muted);font-size:13px;cursor:pointer}
.awp .awp-drop.over{border-color:var(--gold);color:var(--gold)}
.awp .awp-drop img{max-width:100%;max-height:220px;border-radius:4px;display:block}
.awp .awp-row{display:flex;gap:6px;margin-top:8px}
.awp .awp-row>*{flex:1}
.awp input[type=text]{box-sizing:border-box;width:100%;margin-top:8px;background:var(--tint);border:1px dashed var(--line);border-radius:4px;padding:8px 10px}
.awp .awp-note{margin:10px 0 0;font-size:12px;line-height:1.45;color:var(--muted)}
.awp label.awp-ok{display:flex;gap:8px;align-items:flex-start;margin-top:8px;font-size:13px;line-height:1.35}
.awp label.awp-ok input{margin-top:2px}
.awp .awp-st{min-height:1.4em;margin:8px 0 0;font-size:13px;font-weight:600}
.awp .awp-st.awp-err{color:var(--red-l,#ff8a7a)}
.awp .awp-b0{border-color:rgba(60,120,216,.6)} .awp .awp-b1{border-color:rgba(216,68,60,.6)}
.awp [hidden]{display:none!important}
`;
let cssDone = false;
function injectCss() {
  if (cssDone) return; cssDone = true;
  const s = document.createElement('style'); s.textContent = CSS; document.head.appendChild(s);
}
const el = (tag, props = {}, kids = []) => { const e = Object.assign(document.createElement(tag), props); for (const k of kids) e.append(k); return e; };

/**
 * @param {{ mount: HTMLElement, onRecipe: (team: 0|1, r: { recipe: object, source: string, cached: boolean }) => void }} o
 * @returns {{ button: HTMLButtonElement, dialog: HTMLDialogElement, open: () => void, close: () => void, setFile: (f: Blob) => Promise<void> }}
 */
export function createPhotoUpload({ mount, onRecipe, canSend = () => true }) {
  injectCss();
  const coarse = matchMedia('(pointer: coarse)').matches;
  const button = el('button', { className: 'ghost', id: 'genPhoto', type: 'button', textContent: 'Foto', title: 'Einheit aus einem Foto erzeugen (du, Freunde, Haustier, Spielzeug)' });
  mount.appendChild(button);

  const pick = el('input', { type: 'file', accept: 'image/*', hidden: true });
  const cam = el('input', { type: 'file', accept: 'image/*', hidden: true });
  cam.setAttribute('capture', 'user');
  const prevImg = el('img', { alt: 'Vorschau des Fotos', hidden: true });
  const dropText = el('span', { textContent: 'Foto hierher ziehen oder tippen zum Auswählen' });
  const drop = el('div', { className: 'awp-drop', tabIndex: 0, role: 'button' }, [prevImg, dropText]);
  drop.setAttribute('aria-label', 'Foto auswählen');
  const bPick = el('button', { className: 'ghost', type: 'button', textContent: 'Foto wählen' });
  const bCam = el('button', { className: 'ghost', type: 'button', textContent: 'Kamera', hidden: !coarse });
  const hint = el('input', { type: 'text', maxLength: HINT_MAX, autocomplete: 'off', placeholder: 'Wunsch (optional), z. B. mit Laserschwert' });
  hint.setAttribute('aria-label', 'Zusatzwunsch');
  const consent = el('input', { type: 'checkbox' });
  const note = el('p', { className: 'awp-note', textContent:
    'Datenschutz: Das Foto wird in deinem Browser verkleinert (max. 512 px) und ohne Metadaten (z. B. Ort, Kamera) übertragen. ' +
    'Es wird nur verwendet, um diese Figur zu erstellen, dafür an den KI-Dienst des Spiels geschickt und von uns nicht gespeichert. ' +
    'Gespeichert wird nur der fertige Bauplan der Figur.' });
  const st = el('p', { className: 'awp-st', role: 'status' });
  st.setAttribute('aria-live', 'polite');
  const send0 = el('button', { className: 'ghost awp-b0', type: 'button', textContent: 'Für Blau', disabled: true });
  const send1 = el('button', { className: 'ghost awp-b1', type: 'button', textContent: 'Für Rot', disabled: true });
  const bClose = el('button', { className: 'ghost', type: 'button', textContent: 'Schließen' });
  const dialog = el('dialog', { className: 'awp' }, [
    el('h2', { textContent: 'Figur aus Foto' }),
    drop, pick, cam,
    el('div', { className: 'awp-row' }, [bPick, bCam]),
    hint,
    el('label', { className: 'awp-ok' }, [consent, el('span', { textContent: 'Das Foto zeigt mich, ein Tier oder Spielzeug, oder die Person auf dem Foto ist einverstanden.' })]),
    note, st,
    el('div', { className: 'awp-row' }, [send0, send1]),
    el('div', { className: 'awp-row' }, [bClose]),
  ]);
  dialog.setAttribute('aria-label', 'Figur aus Foto');
  document.body.appendChild(dialog);

  let img = null, busy = false, ctrl = null, seq = 0;
  const status = (t, err = false) => { st.textContent = t; st.classList.toggle('awp-err', err); };
  const sync = () => { send0.disabled = send1.disabled = !(img && consent.checked) || busy; bPick.disabled = bCam.disabled = busy; hint.disabled = busy; };
  function reset() {
    img = null; seq++; prevImg.hidden = true; prevImg.removeAttribute('src'); dropText.hidden = false;
    pick.value = ''; cam.value = ''; hint.value = ''; status(''); sync();
  }
  async function setFile(f) {
    if (!f || busy) return;
    const my = ++seq;
    status('Foto wird vorbereitet …'); img = null; sync();
    try {
      const p = await prepareImage(f);
      if (my !== seq) return;
      img = p; prevImg.src = p.dataUrl; prevImg.hidden = false; dropText.hidden = true;
      status(consent.checked ? 'Bereit: wähle die Armee.' : 'Bitte bestätige das Einverständnis.');
    } catch (e) {
      if (my !== seq) return;
      status(e?.message || 'Das Bild konnte nicht gelesen werden.', true);
    } finally { pick.value = ''; cam.value = ''; sync(); }
  }
  async function submit(team) {
    if (!img || !consent.checked || busy) return;
    if (!canSend()) return; // e.g. no free figures and no diamonds left
    busy = true; sync(); ctrl = new AbortController();
    status('Die KI baut deine Figur … (ca. 10–30 Sekunden)');
    try {
      const r = await generatePhoto(img.base64, hint.value, { signal: ctrl.signal });
      if (!dialog.open) return;
      if (r.source === 'model' && r.recipe) {
        onRecipe(team, r);
        dialog.close();
        return;
      }
      status(r.message || 'Aus diesem Foto konnte keine Figur gebaut werden.', true);
    } finally { busy = false; ctrl = null; sync(); }
  }

  button.addEventListener('click', () => { if (!dialog.open) dialog.showModal(); });
  bPick.addEventListener('click', () => pick.click());
  bCam.addEventListener('click', () => cam.click());
  drop.addEventListener('click', () => (coarse ? cam : pick).click());
  drop.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick.click(); } });
  for (const inp of [pick, cam]) inp.addEventListener('change', () => setFile(inp.files && inp.files[0]));
  drop.addEventListener('dragover', e => { e.preventDefault(); drop.classList.add('over'); });
  drop.addEventListener('dragleave', () => drop.classList.remove('over'));
  drop.addEventListener('drop', e => { e.preventDefault(); drop.classList.remove('over'); const f = [...(e.dataTransfer?.files || [])].find(x => /^image\//.test(x.type)); if (f) setFile(f); else status('Bitte ein Bild ablegen.', true); });
  dialog.addEventListener('paste', e => { const f = [...(e.clipboardData?.files || [])].find(x => /^image\//.test(x.type)); if (f) setFile(f); });
  consent.addEventListener('change', () => { if (img) status(consent.checked ? 'Bereit: wähle die Armee.' : 'Bitte bestätige das Einverständnis.'); sync(); });
  hint.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); submit(e.shiftKey ? 1 : 0); } });
  send0.addEventListener('click', () => submit(0));
  send1.addEventListener('click', () => submit(1));
  bClose.addEventListener('click', () => dialog.close());
  // closing (button, Esc, backdrop) cancels a running request and drops the photo from memory
  dialog.addEventListener('close', () => { if (ctrl) ctrl.abort(); reset(); });
  dialog.addEventListener('click', e => {
    if (e.target !== dialog || busy) return;
    const r = dialog.getBoundingClientRect(); // backdrop clicks land on the dialog element outside its box
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
  });

  return { button, dialog, open: () => { if (!dialog.open) dialog.showModal(); }, close: () => dialog.close(), setFile };
}
